import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { StatusBadge } from "@fawkes/ui";
import {
  AlertTriangle, CalendarDays, CheckCircle2, ClipboardList, Clock, Hash, Hourglass, PawPrint, RotateCcw, Search,
  SlidersHorizontal, Sun, UserPlus, UserRound, Users, XCircle, type LucideProps,
} from "lucide-react";

import { formatDateTime } from "../i18n/format";
import { toon, type Aanpassing, type Regel } from "./klantverzoeken/types";
import "./OpvangModules.css";
import "./PleeggezinOverzicht.css";
import "./Klantverzoeken.css";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Klantverzoeken: alles wat klanten via het portaal aanvragen, in dezelfde opbouw als de opvang- en
 * pleeggezinoverzichten (Maarten, 2026-10-04: "ik wil de overzichten zoals bij asiel gebruiken en ik wil ze
 * opgesplitst hebben in wat er gedaan is"). Bovenaan het onderwerp (klanten, dieren, reserveringen, dagopvang),
 * daaronder per soort actie een tab (nieuw, wijzigingen, annuleringen, …) en Afgerond, met tegels per status,
 * filters en kaarten of een lijst. Mislukte verwerkingen staan altijd bovenaan.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;
type Icoon = ComponentType<LucideProps>;

const OPEN = "NIEUW,IN_BEHANDELING,AANVULLING_NODIG,VOORSTEL_KLANT,GOEDGEKEURD,MISLUKT";
const AFGEROND = "VERWERKT,AFGEWEZEN,INGETROKKEN,VERLOPEN";

interface Tab { key: string; types: string[] }
interface Groep { key: string; icoon: Icoon; tabs: Tab[] }

/** Het onderwerp met per soort actie een tab; "alles" en "afgerond" komen er bij iedere groep bij. */
const GROEPEN: Groep[] = [
  { key: "klanten", icoon: Users, tabs: [
    { key: "nieuw", types: ["KLANT_REGISTRATIE"] },
    { key: "wijzigingen", types: ["CONTACTPERSOON_WIJZIGING"] },
    { key: "gezinsleden", types: ["CONTACTPERSOON_TOEVOEGEN"] },
  ] },
  { key: "dieren", icoon: PawPrint, tabs: [
    { key: "nieuw", types: ["DIER_NIEUW"] },
    { key: "wijzigingen", types: ["DIER_WIJZIGING"] },
    { key: "documenten", types: ["DOCUMENT_UPLOAD"] },
  ] },
  { key: "reserveringen", icoon: CalendarDays, tabs: [
    { key: "nieuw", types: ["RESERVERING_NIEUW"] },
    { key: "wachtlijst", types: ["RESERVERING_WACHTLIJST"] },
    { key: "wijzigingen", types: ["RESERVERING_WIJZIGING"] },
    { key: "annuleringen", types: ["RESERVERING_ANNULERING"] },
  ] },
  { key: "dagopvang", icoon: Sun, tabs: [
    { key: "nieuw", types: ["DAGOPVANG_NIEUW"] },
    { key: "wijzigingen", types: ["DAGOPVANG_WIJZIGING"] },
    { key: "afmeldingen", types: ["DAGOPVANG_ANNULERING"] },
  ] },
];
const BEKEND = new Set(GROEPEN.flatMap((g) => g.tabs.flatMap((t) => t.types)));
const OVERIG: Groep = { key: "overig", icoon: ClipboardList, tabs: [] };

const groepTypes = (g: Groep, alle: Regel[]) =>
  g.key === "overig" ? [...new Set(alle.map((r) => r.type).filter((ty) => !BEKEND.has(ty)))] : g.tabs.flatMap((t) => t.types);

/** De statustegels in de kop: open verzoeken per fase, afgeronde per uitkomst. */
const OPEN_FASEN = [
  { key: "nieuw", statussen: ["NIEUW"], icoon: Clock },
  { key: "bezig", statussen: ["IN_BEHANDELING", "GOEDGEKEURD"], icoon: Hourglass },
  { key: "klant", statussen: ["AANVULLING_NODIG", "VOORSTEL_KLANT"], icoon: UserRound },
  { key: "mislukt", statussen: ["MISLUKT"], icoon: AlertTriangle },
] as const;
const AFGEROND_FASEN = [
  { key: "verwerkt", statussen: ["VERWERKT"], icoon: CheckCircle2 },
  { key: "afgewezen", statussen: ["AFGEWEZEN"], icoon: XCircle },
  { key: "vervallen", statussen: ["INGETROKKEN", "VERLOPEN"], icoon: RotateCcw },
] as const;

const KLEUR: Record<string, string> = {
  NIEUW: "#2563eb", IN_BEHANDELING: "#7c3aed", GOEDGEKEURD: "#7c3aed", AANVULLING_NODIG: "#d97706",
  VOORSTEL_KLANT: "#d97706", MISLUKT: "#dc2626", VERWERKT: "#16a34a", AFGEWEZEN: "#6b7280", INGETROKKEN: "#9ca3af",
  VERLOPEN: "#9ca3af",
};

function initialen(naam: string): string {
  const w = naam.replace(/^(fam\.?|familie|mevr\.?|dhr\.?)\s+/i, "").split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? "?") + (w.length > 1 ? w[w.length - 1][0] : "")).toUpperCase();
}

export function Klantverzoeken({ onOpen, onInstellingen }: {
  onOpen: (id: string) => void;
  /** Naar de portaalinstellingen; zonder dit blijft de knop weg. */
  onInstellingen?: () => void;
}) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const [groepKey, setGroepKey] = useState("klanten");
  const [tabKey, setTabKey] = useState("alles");
  const [fase, setFase] = useState("");
  const [zoek, setZoek] = useState("");
  const [kanaal, setKanaal] = useState("");
  const [alleenTeLang, setAlleenTeLang] = useState(false);
  const [alleenNieuweKlanten, setAlleenNieuweKlanten] = useState(false);
  const [weergave, setWeergave] = useState<"kaart" | "lijst">("kaart");
  const [sorteer, setSorteer] = useState("nieuwste");

  const open = useCustom<Regel[]>({ url: `klantverzoeken?status=${OPEN}`, method: "get" });
  const afgerond = useCustom<Regel[]>({
    url: `klantverzoeken?status=${AFGEROND}`,
    method: "get",
    queryOptions: { enabled: tabKey === "afgerond" },
  });
  const openRijen = useMemo(() => (Array.isArray(open.data?.data) ? open.data.data : []), [open.data]);
  const afgerondRijen = useMemo(() => (Array.isArray(afgerond.data?.data) ? afgerond.data.data : []), [afgerond.data]);

  const groepen = useMemo(
    () => (openRijen.some((r) => !BEKEND.has(r.type)) ? [...GROEPEN, OVERIG] : GROEPEN),
    [openRijen],
  );
  const groep = groepen.find((g) => g.key === groepKey) ?? groepen[0];
  const types = groepTypes(groep, openRijen);
  const inGroep = (r: Regel) => types.includes(r.type);
  const tabs = [{ key: "alles", types }, ...groep.tabs, { key: "afgerond", types }];
  const tab = tabs.find((x) => x.key === tabKey) ?? tabs[0];
  const afgerondTab = tab.key === "afgerond";

  // Begin bij een groep waar iets openstaat, als de eerste leeg is.
  useEffect(() => {
    if (openRijen.length && !openRijen.some((r) => groepTypes(GROEPEN[0], openRijen).includes(r.type))) {
      const eerste = groepen.find((g) => openRijen.some((r) => groepTypes(g, openRijen).includes(r.type)));
      if (eerste) setGroepKey(eerste.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRijen.length]);

  const basis = (afgerondTab ? afgerondRijen : openRijen).filter((r) => tab.types.includes(r.type) && inGroep(r));
  const fasen = afgerondTab ? AFGEROND_FASEN : OPEN_FASEN;
  const actieveFase = fasen.find((f) => f.key === fase);
  const z = zoek.trim().toLowerCase();
  const zichtbaar = basis
    .filter((r) => !actieveFase || (actieveFase.statussen as readonly string[]).includes(r.status))
    .filter((r) => !kanaal || r.kanaal === kanaal)
    .filter((r) => !alleenTeLang || r.teLangNieuw)
    .filter((r) => !alleenNieuweKlanten || r.nieuweKlant)
    .filter((r) => !z || [r.nummer, r.klantnaam, r.huishouden, r.samenvatting, r.geclaimdDoor]
      .some((v) => (v ?? "").toLowerCase().includes(z)))
    .sort((a, b) => {
      if (!afgerondTab && (a.status === "MISLUKT") !== (b.status === "MISLUKT")) return a.status === "MISLUKT" ? -1 : 1;
      if (sorteer === "oudste") return a.ingediend.localeCompare(b.ingediend);
      if (sorteer === "klant") return (a.klantnaam ?? a.huishouden ?? "").localeCompare(b.klantnaam ?? b.huishouden ?? "", "nl");
      return b.ingediend.localeCompare(a.ingediend);
    });

  const wis = () => { setZoek(""); setKanaal(""); setAlleenTeLang(false); setAlleenNieuweKlanten(false); setFase(""); };
  const kiesGroep = (key: string) => { setGroepKey(key); setTabKey("alles"); setFase(""); };
  const kiesTab = (key: string) => { setTabKey(key); setFase(""); };
  const laden = afgerondTab ? afgerond.isLoading : open.isLoading;
  const fout = afgerondTab ? afgerond.isError : open.isError;
  const teLang = openRijen.filter((r) => r.teLangNieuw && inGroep(r)).length;
  const GroepIcoon = groep.icoon;

  return (
    <div className="fawkes-pgo kvo">
      <nav className="opvang-modules" aria-label={t("klantverzoeken.titel")}>
        {groepen.map((g) => {
          const Ic = g.icoon;
          const n = openRijen.filter((r) => r.status === "NIEUW" && groepTypes(g, openRijen).includes(r.type)).length;
          return (
            <button key={g.key} type="button" aria-current={g.key === groep.key ? "page" : undefined}
              className={g.key === groep.key ? "opvang-modules__knop is-actief" : "opvang-modules__knop"} onClick={() => kiesGroep(g.key)}>
              <Ic size={16} aria-hidden="true" />
              {t(`klantverzoeken.groep.${g.key}`)}
              {n > 0 && <span className="kvo__badge" title={t("klantverzoeken.nieuwTeBeoordelen", { args: [n] })}>{n}</span>}
            </button>
          );
        })}
      </nav>

      <header className="pg__head">
        <div className="pg__titel">
          <div className="pg__avatar"><GroepIcoon size={22} aria-hidden="true" /></div>
          <div>
            <span className="pg__overline">{t("klantverzoeken.titel")}</span>
            <h1>{t(`klantverzoeken.groep.${groep.key}`)}</h1>
            <span className="pg__vestiging">
              {teLang > 0 && !afgerondTab
                ? t("klantverzoeken.subtitelTeLang", { args: [teLang] })
                : t(`klantverzoeken.groepUitleg.${groep.key}`)}
            </span>
          </div>
        </div>
        <div className="soorten">
          {fasen.map((f) => {
            const Ic = f.icoon;
            const n = basis.filter((r) => (f.statussen as readonly string[]).includes(r.status)).length;
            return (
              <button key={f.key} type="button" className={`soortKaart kvo__fase kvo__fase--${f.key}` + (fase === f.key ? " actief" : "")}
                onClick={() => setFase(fase === f.key ? "" : f.key)}>
                <span className="soortIcon"><Ic size={20} aria-hidden="true" /></span>
                <span className="soortBody"><span className="soortAantal">{n}</span><span className="soortNaam">{t(`klantverzoeken.fase.${f.key}`)}</span></span>
              </button>
            );
          })}
          <button type="button" className={"soortKaart totaal" + (!fase ? " actief" : "")} onClick={() => setFase("")}>
            <span className="soortIcon"><GroepIcoon size={20} aria-hidden="true" /></span>
            <span className="soortBody"><span className="soortAantal">{basis.length}</span><span className="soortNaam">{t("klantverzoeken.fase.totaal")}</span></span>
          </button>
        </div>
        {/* Onder de cijfers, want daar kijk je al: wat er binnenkomt hangt af van wat er in het portaal
            aanstaat, dus de instellingen horen binnen handbereik van dit scherm. */}
        {onInstellingen && (
          <div className="kvo__beheer">
            <button type="button" className="kvo__instellingen" onClick={onInstellingen}>
              <SlidersHorizontal size={16} aria-hidden="true" />
              {t("menu.portaalInstellingen")}
            </button>
          </div>
        )}
      </header>

      <nav className="tabbar" role="tablist">
        {tabs.map((x) => {
          const n = x.key === "afgerond" ? null
            : openRijen.filter((r) => r.status === "NIEUW" && x.types.includes(r.type) && inGroep(r)).length;
          return (
            <button key={x.key} type="button" role="tab" aria-selected={x.key === tab.key}
              className={"tabbar__tab" + (x.key === tab.key ? " is-active" : "")} onClick={() => kiesTab(x.key)}>
              {t(`klantverzoeken.tab.${x.key}`)}
              {n != null && n > 0 && <span className="tabbar__badge kvo__tabbadge">{n}</span>}
            </button>
          );
        })}
      </nav>

      <div className="pg__body">
        <section className="filters">
          <div className="pgFilterRij">
            <div className="zoek">
              <span className="zoekIcon"><Search size={16} aria-hidden="true" /></span>
              <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={t("klantverzoeken.zoek")} aria-label={t("klantverzoeken.zoek")} />
            </div>
            <label className="veld"><span>{t("klantverzoeken.kolom.kanaal")}</span>
              <select value={kanaal} onChange={(e) => setKanaal(e.target.value)}>
                <option value="">{t("klantverzoeken.alle")}</option>
                <option value="PORTAAL">{t("klantverzoeken.kanaal.PORTAAL")}</option>
                <option value="EXTERNE_API">{t("klantverzoeken.kanaal.EXTERNE_API")}</option>
              </select>
            </label>
            <div className="pgToggles">
              {!afgerondTab && (
                <button type="button" className={"toggleChip" + (alleenTeLang ? " aan" : "")} onClick={() => setAlleenTeLang((v) => !v)}>
                  <span className="toggleDot" /> {t("klantverzoeken.filterTeLang")}
                </button>
              )}
              <button type="button" className={"toggleChip" + (alleenNieuweKlanten ? " aan" : "")} onClick={() => setAlleenNieuweKlanten((v) => !v)}>
                <span className="toggleDot" /> {t("klantverzoeken.nieuweKlanten")}
              </button>
            </div>
            <button type="button" className="wisBtn" onClick={wis}><span className="wisIcon">↻</span> {t("pgOverzicht.wisFilters")}</button>
          </div>
        </section>

        <div className="metaBar">
          <span className="resultaten">
            <ClipboardList size={16} aria-hidden="true" />
            {t("klantverzoeken.resultaten", { args: [zichtbaar.length] })}
          </span>
          <div className="rechts">
            <div className="weergaveTabs">
              <button type="button" className={weergave === "kaart" ? "actief" : ""} onClick={() => setWeergave("kaart")} title={t("pgOverzicht.weergave.kaarten")}>▦</button>
              <button type="button" className={weergave === "lijst" ? "actief" : ""} onClick={() => setWeergave("lijst")} title={t("pgOverzicht.weergave.lijst")}>☰</button>
            </div>
            <label className="metaSort"><span>{t("pgOverzicht.sorterenOp")}</span>
              <select value={sorteer} onChange={(e) => setSorteer(e.target.value)}>
                <option value="nieuwste">{t("klantverzoeken.sorteer.nieuwste")}</option>
                <option value="oudste">{t("klantverzoeken.sorteer.oudste")}</option>
                <option value="klant">{t("klantverzoeken.sorteer.klant")}</option>
              </select>
            </label>
          </div>
        </div>

        {fout && <p className="fawkes-error" role="alert">{t("klantverzoeken.fout.laden")}</p>}
        {laden ? <p className="leeg">{t("common.loading")}</p> : zichtbaar.length === 0 ? (
          <p className="leeg">{basis.length === 0 ? t("klantverzoeken.leeg") : t("klantverzoeken.leegFilter")}</p>
        ) : weergave === "lijst" ? (
          <section className="lijst">
            <IndeelbareTabel
              tabelKey="klantverzoeken"
              columns={[
                {
                  key: "klant", header: t("klantverzoeken.kolom.klant"),
                  // Het gekleurde streepje links is de status in een oogopslag.
                  cell: (r) => (
                    <span className="lnaam" style={{ boxShadow: `inset 4px 0 0 ${KLEUR[r.status] ?? "#9aa6a0"}` }}>
                      <div className="ldierTekst">
                        <b>{r.klantnaam ?? r.huishouden ?? "—"}</b>
                        {r.nieuweKlant && <span>{t("klantverzoeken.nieuweKlant")}</span>}
                      </div>
                    </span>
                  ),
                },
                {
                  key: "status", header: t("klantverzoeken.kolom.status"),
                  cell: (r) => (
                    <StatusBadge tone={toon(r.status)}>{t(`klantverzoeken.status.${r.status}`)}</StatusBadge>
                  ),
                },
                { key: "type", header: t("klantverzoeken.kolom.type"), cell: (r) => t(`klantverzoeken.type.${r.type}`) },
                {
                  key: "wat", header: t("klantverzoeken.kolom.wat"),
                  cell: (r) => (
                    <span className="kvo__aanvraag">
                      {r.samenvatting && <b>{r.samenvatting}</b>}
                      <Aanpassingen t={t} velden={r.velden} max={3} />
                      {!r.samenvatting && !r.velden?.length && "—"}
                    </span>
                  ),
                },
                { key: "nummer", header: t("klantverzoeken.kolom.nummer"),
                  cell: (r) => <span className="kv__nummer">{r.nummer}</span> },
                {
                  key: "ingediend", header: t("klantverzoeken.kolom.ingediend"),
                  cell: (r) => (
                    <span className={r.teLangNieuw ? "kv__telang" : undefined}>
                      {r.teLangNieuw && <AlertTriangle size={13} aria-label={t("klantverzoeken.teLang")} />}{" "}
                      {formatDateTime(r.ingediend, locale)}
                    </span>
                  ),
                },
                { key: "bij", header: t("klantverzoeken.kolom.bij"), cell: (r) => r.geclaimdDoor ?? "" },
              ]}
              rows={zichtbaar}
              getRowKey={(r) => String(r.id)}
              onRowClick={(r) => onOpen(r.id)}
              rowClassName={(r) => (r.status === "MISLUKT" ? "kv__rij--mislukt" : undefined)}
              empty=""
            />
          </section>
        ) : (
          <div className="relaties">
            {zichtbaar.map((r) => <VerzoekKaart key={r.id} r={r} t={t} locale={locale} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Een veldnaam uit de aanvraag als label; onbekende namen worden leesbaar gemaakt ("aankomstTijd" → "Aankomst tijd"). */
function veldLabel(t: T, naam: string): string {
  const sleutel = `klantverzoeken.veld.${naam}`;
  const vertaald = t(sleutel);
  if (vertaald && vertaald !== sleutel) return vertaald;
  const woorden = naam.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return woorden.charAt(0).toUpperCase() + woorden.slice(1);
}

/** Wat er gewijzigd is: het oude doorgestreept, het nieuwe vet. Bij iets nieuws alleen de ingevulde waarde. */
function Aanpassingen({ t, velden, max }: { t: T; velden?: Aanpassing[]; max: number }) {
  if (!velden?.length) return null;
  const meer = velden.length - max;
  return (
    <ul className="kvo__velden">
      {velden.slice(0, max).map((v) => {
        const gewijzigd = v.oud != null || v.nieuw == null;
        return (
          <li key={v.naam} className={gewijzigd ? "kvo__veld kvo__veld--gewijzigd" : "kvo__veld"}>
            <span className="kvo__veldnaam">{veldLabel(t, v.naam)}</span>
            {v.oud != null && <><s className="kvo__oud">{v.oud}</s><span className="kvo__pijl" aria-hidden="true">→</span></>}
            {v.nieuw != null ? <span className="kvo__nieuw">{v.nieuw}</span> : <em className="kvo__leeggemaakt">{t("klantverzoeken.leeggemaakt")}</em>}
          </li>
        );
      })}
      {meer > 0 && <li className="kvo__meer">{t("klantverzoeken.meerVelden", { args: [meer] })}</li>}
    </ul>
  );
}

function VerzoekKaart({ r, t, locale, onOpen }: { r: Regel; t: T; locale: string; onOpen: (id: string) => void }) {
  const kleur = KLEUR[r.status] ?? "#9aa6a0";
  const naam = r.klantnaam ?? r.huishouden ?? "—";
  return (
    <button type="button" className="relatie kvo__kaart" style={{ borderLeft: `5px solid ${kleur}` }} onClick={() => onOpen(r.id)}>
      <div className="relatie__hoofd">
        <div className="relatie__avatar" style={{ background: kleur + "22", color: kleur }}>{initialen(naam)}</div>
        <div className="relatie__id">
          <span className="relatie__naam">{naam}</span>
          <span className="relatie__rol">{t(`klantverzoeken.type.${r.type}`)}</span>
        </div>
        <StatusBadge tone={toon(r.status)}>{t(`klantverzoeken.status.${r.status}`)}</StatusBadge>
      </div>
      <div className="relatie__contact">
        {r.samenvatting && <div className="relatie__regel kvo__wat"><ClipboardList size={15} className="mi" aria-hidden="true" /><span>{r.samenvatting}</span></div>}
        <Aanpassingen t={t} velden={r.velden} max={4} />
        <div className="relatie__regel"><Hash size={15} className="mi" aria-hidden="true" /><span>{r.nummer}</span></div>
        <div className={"relatie__regel" + (r.teLangNieuw ? " kv__telang" : "")}>
          {r.teLangNieuw ? <AlertTriangle size={15} className="mi" aria-hidden="true" /> : <Clock size={15} className="mi" aria-hidden="true" />}
          <span>{formatDateTime(r.ingediend, locale)}{r.teLangNieuw ? ` · ${t("klantverzoeken.teLang")}` : ""}</span>
        </div>
        {r.geclaimdDoor && <div className="relatie__regel"><UserRound size={15} className="mi" aria-hidden="true" /><span>{t("klantverzoeken.bijIemand", { args: [r.geclaimdDoor] })}</span></div>}
      </div>
      <div className="kvo__voet">
        <span className="chip-tag chip-tag--groen">{t(`klantverzoeken.kanaal.${r.kanaal}`)}</span>
        {r.nieuweKlant && <span className="kv__nieuw"><UserPlus size={13} aria-hidden="true" /> {t("klantverzoeken.nieuweKlant")}</span>}
      </div>
    </button>
  );
}
